import { type IDataObject } from '@inpart-io/devtools-iap';

/**
 * Checks if the given target is a data object of type `T` that extends `IDataObject`.
 *
 * @template T - The type that extends `IDataObject`.
 * @param target - The object to check.
 * @param attribute - The attribute to look for in the target object. Defaults to 'dobjId'.
 * @returns `true` if the target is an object and contains the specified attribute, otherwise `false`.
 */
export function isDataObject<T extends IDataObject>(target: unknown, attribute = 'dobjId'): target is T {
    return typeof target === 'object' && target !== null && attribute in target;
}

/**
 * Gets the data object ID of the given target.
 * If the target is a data object, the `dobjId` property is returned.
 *
 * @template T - The type that extends `IDataObject`.
 * @param target - The target to get the data object ID from.
 * @returns The data object ID if the target is a data object, otherwise `undefined`.
 */
export function getDobjId<T extends IDataObject>(target: T | number): number | undefined {
    if (isDataObject(target)) {
        return target.dobjId;
    } else if (typeof target === 'number') {
        return target;
    } else {
        return undefined;
    }
}
